import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';

interface TablePaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  itemLabel?: string;
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
}

export function TablePagination({
  currentPage = 1,
  totalItems = 0,
  pageSize = 10,
  onPageChange,
  itemLabel = 'mục',
  pageSizeOptions = [10, 20, 30],
  onPageSizeChange,
  className = '',
}: TablePaginationProps) {
  const safePageSize = Math.max(1, pageSize);
  const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize));
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * safePageSize + 1;
  const endItem = Math.min(currentPage * safePageSize, totalItems);

  // Logic to calculate which page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage, '...', totalPages);
      }
    }
    return pages;
  };

  return (
    <div className={cn('flex flex-col items-center justify-between gap-4 py-2 lg:flex-row w-full', className)}>
      <div className="sm:text-sm text-xs text-slate-500 order-2 lg:order-1">
        Hiển thị <span className="text-slate-700">{startItem} - {endItem}</span> trong <span className="text-slate-700">{totalItems}</span> {itemLabel}
      </div>

      <div className="flex items-center gap-2 order-1 lg:order-2">
        <div className="mr-2 hidden sm:flex items-center gap-1 text-xs text-slate-400">
          Trang <span className="text-emerald-700">{currentPage}</span> / {totalPages}
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="flex h-8 w-8 lg:h-9 lg:w-9 cursor-pointer items-center justify-center rounded-md border border-slate-100 bg-white text-slate-400 transition-all hover:border-slate-300 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4 lg:w-5 lg:h-5" />
          </button>

          <div className="flex items-center gap-1">
            {getPageNumbers().map((page, index) => {
              if (page === '...') {
                return <span key={`dots-${index}`} className="px-1 text-slate-400">...</span>;
              }
              const isActive = page === currentPage;
              return (
                <button
                  key={page}
                  type="button"
                  onClick={() => onPageChange(page as number)}
                  className={cn(
                    'flex h-8 w-8 lg:h-9 lg:w-9 cursor-pointer items-center justify-center rounded-md text-xs transition-all',
                    isActive
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'border border-slate-100 bg-white text-slate-600 hover:border-slate-300'
                  )}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="flex h-8 w-8 lg:h-9 lg:w-9 cursor-pointer items-center justify-center rounded-md border border-slate-100 bg-white text-slate-400 transition-all hover:border-slate-300 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
